import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Pin, PinOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ListSkeleton } from '@/components/ui/skeleton';
import { salesOrderService } from '../../services/salesOrderService';
import {
  SalesOrderEventType,
  type SalesOrderListItemDto,
} from '../../types/salesOrder.types';
import {
  type FilterTab,
  useSalesOrderWorkspaceStore,
} from '../../store/salesOrderWorkspaceStore';
import {
  onSalesOrderEvent,
  onSalesOrderStreamReconnect,
} from '../../hooks/useSalesOrderEventStream';
import { SalesOrderStatusBadge } from '../SalesOrderStatusBadge';
import { statusesForFilter, rowMatchesFilter } from '../../lib/listFilter';

interface Props {
  statusFilter: FilterTab;
  quickFilter: string;
  selectedId: number | null;
  onSelect: (id: number) => void;
}

// Master pane row list — stale-while-revalidate fetch + SSE-driven splice.
// Mirrors the existing SalesOrderListPage flow (lines 49-86) but renders as a
// single-column row list rather than a table so it fits the narrow master pane.

export function SalesOrderMasterList({
  statusFilter,
  quickFilter,
  selectedId,
  onSelect,
}: Props) {
  const [rows, setRows] = useState<SalesOrderListItemDto[]>([]);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqIdRef = useRef(0);

  const pinnedIds = useSalesOrderWorkspaceStore((s) => s.pinnedIds);
  const togglePin = useSalesOrderWorkspaceStore((s) => s.togglePin);

  const refresh = useCallback(async () => {
    const reqId = ++reqIdRef.current;
    setIsFetching(true);
    setError(null);
    try {
      const params: Parameters<typeof salesOrderService.getPaged>[0] = {
        page: 1,
        pageSize: 50,
        sortBy: 'updatedAt',
        sortDesc: true,
        search: quickFilter.trim() || undefined,
        includeArchived: statusFilter === 'archived',
      };
      const statuses = statusesForFilter(statusFilter);
      if (statuses) params.statuses = statuses;
      const result = await salesOrderService.getPaged(params);
      // Drop stale responses if the user switched filters mid-flight.
      if (reqId !== reqIdRef.current) return;
      setRows(result.items);
      setHasLoadedOnce(true);
    } catch (err) {
      if (reqId !== reqIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load sales orders');
    } finally {
      if (reqId === reqIdRef.current) setIsFetching(false);
    }
  }, [statusFilter, quickFilter]);

  // Refetch on filter / search change. Per-row updates (saves, status changes)
  // arrive via the SSE splice below — we deliberately don't refetch on save, which
  // used to flicker the whole list on each autosave (86d3acbub).
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // SSE wiring — splice updates, remove deletes, refetch on reconnect for
  // catch-up. Matches SalesOrderListPage:63-86 behavior.
  useEffect(() => {
    const off = onSalesOrderEvent(async (evt) => {
      if (evt.eventType === SalesOrderEventType.Deleted) {
        setRows((prev) => prev.filter((r) => r.id !== evt.salesOrderId));
        // Drop a deleted row from the pin strip so it can't linger as a dead chip.
        const ws = useSalesOrderWorkspaceStore.getState();
        if (ws.isPinned(evt.salesOrderId)) ws.togglePin(evt.salesOrderId);
        return;
      }
      if (evt.salesOrderId === 0) {
        void refresh();
        return;
      }
      try {
        const row = await salesOrderService.getRow(evt.salesOrderId);
        setRows((prev) => {
          const idx = prev.findIndex((r) => r.id === row.id);
          // 86d3bujyr — gate BOTH directions on filter membership. If the update pushed the
          // row out of the active filter (e.g. a draft that just posted → Submitted leaves the
          // drafts tab; an archived row leaves drafts/submitted), remove it; otherwise splice
          // in place / insert. Prevents a non-matching row from lingering OR appearing.
          if (!rowMatchesFilter(row, statusFilter)) {
            return idx === -1 ? prev : prev.filter((r) => r.id !== row.id);
          }
          if (idx === -1) return [row, ...prev];
          const next = [...prev];
          next[idx] = row;
          return next;
        });
      } catch {
        // getRow failed (row deleted / no longer visible) — drop it.
        setRows((prev) => prev.filter((r) => r.id !== evt.salesOrderId));
      }
    });
    const offReconnect = onSalesOrderStreamReconnect(() => {
      void refresh();
    });
    return () => {
      off();
      offReconnect();
    };
  }, [refresh, statusFilter]);

  // 86d3cxj7n — SSE-independent self-heal. The list learns about new rows from only the
  // initial fetch + the echoed SSE `Updated` event; a freshly-created draft is added to the
  // creator's own list ONLY if that round-trip echo arrives. A transient EventSource gap
  // (reconnect / token refresh / drop) therefore left a SAVED draft invisible. So if the
  // SELECTED draft isn't in the list yet, fetch its row directly and splice it — no echo
  // needed. Also surfaces a deep-linked draft that isn't on the first fetched page. Guarded
  // by a composite `tab:id` key so it fires at most once per selection-per-tab (the effect
  // re-runs on every `rows` change), and the in-flight result is dropped if the selection /
  // tab moved on. NOTE: `rowMatchesFilter` gates on status/archived only — it ignores the
  // search box (quickFilter), exactly like the SSE splice above, so the open draft stays
  // visible regardless of a stale search term.
  const healedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedId) return; // unsaved new draft (id promotes to a real id on first save)
    if (rows.some((r) => r.id === selectedId)) return; // already listed
    const key = `${statusFilter}:${selectedId}`;
    if (healedKeyRef.current === key) return; // already attempted this selection on this tab
    healedKeyRef.current = key;
    const target = selectedId;
    void (async () => {
      try {
        const row = await salesOrderService.getRow(target);
        if (healedKeyRef.current !== key) return; // selection / tab changed mid-flight
        if (!rowMatchesFilter(row, statusFilter)) return; // doesn't belong on this tab
        setRows((prev) => (prev.some((r) => r.id === row.id) ? prev : [row, ...prev]));
      } catch {
        // 404 / transient — keep the guard set so we don't refetch on every render.
      }
    })();
  }, [selectedId, rows, statusFilter]);

  // Initial-load skeleton shaped like the row list; later refetches show a small
  // inline pill so the user can keep scanning prior rows (stale-while-revalidate).
  if (!hasLoadedOnce && isFetching) {
    return (
      <div className="flex-1 overflow-y-auto px-3 py-2">
        <ListSkeleton rows={8} />
      </div>
    );
  }

  if (error && rows.length === 0) {
    return <div className="p-4 text-sm text-destructive">{error}</div>;
  }

  return (
    <div className="relative flex-1 overflow-y-auto pb-16">
      {isFetching && hasLoadedOnce && (
        <div className="sticky top-0 z-10 flex items-center justify-center bg-background/80 py-1 text-[10px] uppercase tracking-wide text-muted-foreground backdrop-blur">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          Updating…
        </div>
      )}
      {rows.length === 0 ? (
        <div className="px-4 py-10 text-center text-sm text-muted-foreground">
          No sales orders match the current filter.
        </div>
      ) : (
        <ul className="divide-y">
          {rows.map((row) => {
            const isSelected = row.id === selectedId;
            const isPinned = pinnedIds.includes(row.id);
            return (
              <li key={row.id}>
                {/* Row is a div(role=button) rather than a <button> so the pin
                    toggle can nest as a real <button> (no nested-button HTML). */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect(row.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(row.id);
                    }
                  }}
                  className={cn(
                    // Transparent left rail by default keeps the card width constant
                    // between active/inactive; active uses the neutral accent fill +
                    // primary rail — matches the Quotation drafts list (86d37ef09).
                    'group flex w-full cursor-pointer flex-col gap-1 border-l-4 border-l-transparent px-3 py-2 text-left transition-colors outline-none focus-visible:bg-accent/40',
                    isSelected && 'border-l-primary bg-accent',
                    !isSelected && 'hover:bg-accent/30',
                    // The left rail signals selection only. A failed post is
                    // conveyed by the status badge alone — a destructive rail
                    // here collides with the primary (selected) rail since both
                    // render red in the Capitol theme, making it read as a
                    // second/third "active" card. (Reverts the rail/tint from
                    // 86d3ah33r; badge is sufficient.)
                  )}
                  aria-current={isSelected ? 'true' : undefined}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate font-mono text-xs text-foreground">
                      {row.salesOrderNumber}
                    </span>
                    <div className="flex items-center gap-1">
                      {/* Pin toggle — visible on hover, or always when pinned.
                          stopPropagation so it doesn't also open the row. */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePin(row.id);
                        }}
                        className={cn(
                          'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                          isPinned
                            ? 'text-primary opacity-100'
                            : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
                        )}
                        aria-label={isPinned ? 'Unpin' : 'Pin'}
                        title={isPinned ? 'Unpin' : 'Pin'}
                      >
                        {isPinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
                      </button>
                      <SalesOrderStatusBadge status={row.status} />
                    </div>
                  </div>
                  <div className="truncate text-sm font-medium text-foreground">
                    {row.customerName || '—'}
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span className="truncate">{row.projectName || '—'}</span>
                    <span className="shrink-0 font-mono">
                      {row.linkedFirmOfferTranId || '—'}
                    </span>
                  </div>
                  {row.netSuiteTranId && (
                    <div className="text-[10px] font-mono text-muted-foreground">
                      NS: {row.netSuiteTranId}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
